import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { PagedResult } from '../../core/models/api.models';
import {
  CustomFieldDefinitionDto,
  DeployVersionRequest,
  IssueCustomFieldValueDto,
  IssueSearchHit,
  IssueVersionLinkDto,
  ProjectMilestoneDto,
  ProjectVersionDto,
  ReleaseCalendarDto,
  RoadmapDto,
  SavedIssueFilterDto,
  SetIssueCustomFieldValueRequest,
  ProjectTimelineDto,
  ReleaseChecklistItemDto,
  SharedRoadmapDto,
  ShareRoadmapResultDto,
  TimelineItemDto,
  TimelineZoom,
  PortfolioSummaryDto,
  PortfolioRoadmapDto,
  PortfolioTimelineDto
} from './pm-advanced.models';

@Injectable({ providedIn: 'root' })
export class PmAdvancedApiService {
  private readonly api = inject(ApiService);
  private readonly base = 'v1/pm';

  listVersions(projectId: string): Observable<ProjectVersionDto[]> {
    return this.api.get<ProjectVersionDto[]>(`${this.base}/projects/${projectId}/versions`);
  }

  createVersion(body: {
    projectId: string;
    name: string;
    description?: string;
    startDate?: string;
    releaseDate?: string;
  }): Observable<ProjectVersionDto> {
    return this.api.post<ProjectVersionDto>(`${this.base}/versions`, body);
  }

  updateVersion(id: string, body: {
    name: string;
    description?: string;
    startDate?: string;
    releaseDate?: string;
    status: number;
    sortOrder: number;
  }): Observable<ProjectVersionDto> {
    return this.api.put<ProjectVersionDto>(`${this.base}/versions/${id}`, body);
  }

  deleteVersion(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/versions/${id}`);
  }

  listCustomFields(projectId?: string): Observable<CustomFieldDefinitionDto[]> {
    return this.api.get<CustomFieldDefinitionDto[]>(`${this.base}/custom-fields`, projectId ? { projectId } : undefined);
  }

  createCustomField(body: {
    projectId?: string;
    name: string;
    key: string;
    fieldType: number;
    description?: string;
    optionsJson?: string;
    isRequired?: boolean;
  }): Observable<CustomFieldDefinitionDto> {
    return this.api.post<CustomFieldDefinitionDto>(`${this.base}/custom-fields`, body);
  }

  deleteCustomField(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/custom-fields/${id}`);
  }

  search(params: { jql?: string; projectId?: string; savedFilterId?: string; skip?: number; take?: number }): Observable<PagedResult<IssueSearchHit>> {
    return this.api.getPaged<IssueSearchHit>(`${this.base}/issues/search`, params);
  }

  listFilters(projectId?: string): Observable<SavedIssueFilterDto[]> {
    return this.api.get<SavedIssueFilterDto[]>(`${this.base}/saved-filters`, projectId ? { projectId } : undefined);
  }

  createFilter(body: {
    name: string;
    jql: string;
    projectId?: string;
    description?: string;
    isShared?: boolean;
  }): Observable<SavedIssueFilterDto> {
    return this.api.post<SavedIssueFilterDto>(`${this.base}/saved-filters`, body);
  }

  deleteFilter(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/saved-filters/${id}`);
  }

  updateFilter(id: string, body: {
    name: string;
    jql: string;
    description?: string;
    isShared?: boolean;
  }): Observable<SavedIssueFilterDto> {
    return this.api.put<SavedIssueFilterDto>(`${this.base}/saved-filters/${id}`, body);
  }

  getReleaseCalendar(projectId: string, from?: string, to?: string): Observable<ReleaseCalendarDto> {
    const params: Record<string, string> = {};
    if (from) params['from'] = from;
    if (to) params['to'] = to;
    return this.api.get<ReleaseCalendarDto>(`${this.base}/projects/${projectId}/release-calendar`, params);
  }

  deployVersion(id: string, body?: DeployVersionRequest): Observable<ProjectVersionDto> {
    return this.api.post<ProjectVersionDto>(`${this.base}/versions/${id}/deploy`, {
      environment: body?.environment ?? 'production',
      deploymentStatus: body?.deploymentStatus ?? 2
    });
  }

  getIssueCustomFields(issueId: string): Observable<IssueCustomFieldValueDto[]> {
    return this.api.get<IssueCustomFieldValueDto[]>(`${this.base}/issues/${issueId}/custom-fields`);
  }

  setIssueCustomFields(issueId: string, values: SetIssueCustomFieldValueRequest[]): Observable<unknown> {
    return this.api.put(`${this.base}/issues/${issueId}/custom-fields`, { values });
  }

  getRoadmap(projectId: string): Observable<RoadmapDto> {
    return this.api.get<RoadmapDto>(`${this.base}/projects/${projectId}/roadmap`);
  }

  listMilestones(projectId: string): Observable<ProjectMilestoneDto[]> {
    return this.api.get<ProjectMilestoneDto[]>(`${this.base}/projects/${projectId}/milestones`);
  }

  createMilestone(body: {
    projectId: string;
    versionId?: string;
    name: string;
    description?: string;
    targetDate?: string;
    status?: number;
    sortOrder?: number;
  }): Observable<ProjectMilestoneDto> {
    return this.api.post<ProjectMilestoneDto>(`${this.base}/projects/${body.projectId}/milestones`, body);
  }

  approveVersion(id: string): Observable<ProjectVersionDto> {
    return this.api.post<ProjectVersionDto>(`${this.base}/versions/${id}/approve`, {});
  }

  rejectVersion(id: string, reason?: string): Observable<ProjectVersionDto> {
    return this.api.post<ProjectVersionDto>(`${this.base}/versions/${id}/reject`, { reason });
  }

  listChecklist(versionId: string): Observable<ReleaseChecklistItemDto[]> {
    return this.api.get<ReleaseChecklistItemDto[]>(`${this.base}/versions/${versionId}/checklist`);
  }

  addChecklistItem(versionId: string, title: string): Observable<ReleaseChecklistItemDto> {
    return this.api.post<ReleaseChecklistItemDto>(`${this.base}/versions/${versionId}/checklist`, { title });
  }

  toggleChecklistItem(versionId: string, itemId: string): Observable<ReleaseChecklistItemDto> {
    return this.api.post<ReleaseChecklistItemDto>(`${this.base}/versions/${versionId}/checklist/${itemId}/toggle`, {});
  }

  deleteChecklistItem(versionId: string, itemId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/versions/${versionId}/checklist/${itemId}`);
  }

  updateMilestone(id: string, body: {
    name: string;
    description?: string;
    targetDate?: string;
    versionId?: string;
    status: number;
    sortOrder: number;
  }): Observable<ProjectMilestoneDto> {
    return this.api.put<ProjectMilestoneDto>(`${this.base}/milestones/${id}`, body);
  }

  deleteMilestone(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/milestones/${id}`);
  }

  releaseVersion(id: string, body?: { releaseNotes?: string }): Observable<ProjectVersionDto> {
    return this.api.post<ProjectVersionDto>(`${this.base}/versions/${id}/release`, body ?? {});
  }

  getIssueVersions(issueId: string): Observable<IssueVersionLinkDto[]> {
    return this.api.get<IssueVersionLinkDto[]>(`${this.base}/issues/${issueId}/versions`);
  }

  setIssueVersions(issueId: string, body: {
    affectsVersionIds?: string[];
    fixVersionIds?: string[];
  }): Observable<unknown> {
    return this.api.put(`${this.base}/issues/${issueId}/versions`, body);
  }

  getTimeline(projectId: string, zoom: TimelineZoom = 'Month'): Observable<ProjectTimelineDto> {
    return this.api.get<ProjectTimelineDto>(`${this.base}/projects/${projectId}/timeline`, { zoom });
  }

  updateTimelineItem(id: string, body: { startDate?: string; endDate?: string; dueDate?: string; title?: string }): Observable<TimelineItemDto> {
    return this.api.put<TimelineItemDto>(`${this.base}/timeline/items/${id}`, {
      startDate: body.startDate,
      dueDate: body.dueDate ?? body.endDate,
      title: body.title
    });
  }

  shareRoadmap(projectId: string): Observable<ShareRoadmapResultDto> {
    return this.api.post<ShareRoadmapResultDto>(`${this.base}/projects/${projectId}/roadmap/share`, {});
  }

  getSharedRoadmap(token: string): Observable<SharedRoadmapDto> {
    return this.api.get<SharedRoadmapDto>(`${this.base}/roadmap/shared/${token}`);
  }

  listPortfolios(): Observable<PortfolioSummaryDto[]> {
    return this.api.get<PortfolioSummaryDto[]>(`${this.base}/portfolios`);
  }

  getPortfolioRoadmap(accountId: string): Observable<PortfolioRoadmapDto> {
    return this.api.get<PortfolioRoadmapDto>(`${this.base}/portfolios/${accountId}/roadmap`);
  }

  getPortfolioTimeline(accountId: string, zoom: TimelineZoom = 'Month', from?: string, to?: string): Observable<PortfolioTimelineDto> {
    const params: Record<string, string> = { zoom };
    if (from) params['from'] = from;
    if (to) params['to'] = to;
    return this.api.get<PortfolioTimelineDto>(`${this.base}/portfolios/${accountId}/timeline`, params);
  }
}
